import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();
  const [added, setAdded] = React.useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (product.stock <= 0) return;

    const result = await addToCart(product.id);
    if (result.success) {
      setAdded(true);
      toast.success('Added to cart');
      setTimeout(() => setAdded(false), 2000);
    } else {
      toast.error(result.error || 'Failed to add item');
    }
  };

  const isOutOfStock = product.stock <= 0;

  return (
    <Link 
      to={`/product/${product.id}`}
      className="group block clean-card transition-all duration-500"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden image-zoom">
        <img 
          src={product.image_url} 
          alt={product.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Stock Badge */}
        {isOutOfStock && (
          <div className="absolute top-4 left-4 bg-[#0A0A0A] text-white text-xs uppercase tracking-wide px-3 py-1">
            Out of Stock
          </div>
        )}
        
        {/* Featured Badge */}
        {product.featured && !isOutOfStock && (
          <div className="absolute top-4 left-4 bg-white text-[#0A0A0A] text-xs uppercase tracking-wide px-3 py-1">
            Featured
          </div>
        )}

        {/* Quick Add Button */}
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Button
            onClick={handleAddToCart}
            disabled={loading || isOutOfStock}
            className={`w-full rounded-none h-11 btn-lift ${
              added 
                ? 'bg-green-600 hover:bg-green-600' 
                : 'bg-[#0A0A0A] hover:bg-[#262626]'
            } text-white disabled:bg-[#A3A3A3]`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {added ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Added
              </>
            ) : (
              <>
                <ShoppingBag className="w-4 h-4 mr-2" />
                {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Product Info */}
      <div className="pt-4 space-y-1">
        <h3 className="font-serif text-lg font-medium text-[#0A0A0A] group-hover:opacity-70 transition-opacity">
          {product.name}
        </h3>
        <p className="text-[#737373] text-sm tracking-wide">
          ${product.price.toFixed(2)}
        </p>
        {product.stock > 0 && product.stock <= 5 && (
          <p className="text-xs text-orange-600">Only {product.stock} left</p>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
